import React from 'react';
import { Caption } from '../common/Typography';

/**
 * Compact guest summary for home / profile merge flows.
 */
export default function GuestAccountSummaryCard({
  guest,
  selected = false,
  selectable = false,
  onSelect,
  clubsLabel = null,
  compact = false,
}) {
  const name = `${guest.first_name || ''} ${guest.last_name || ''}`.trim() || 'Guest';
  const gameCount = guest.game_count ?? 0;
  const sessionCount = guest.total_sessions ?? 0;
  const dateFrom = guest.first_game_date ? new Date(guest.first_game_date).toLocaleDateString() : null;
  const dateTo = guest.last_game_date ? new Date(guest.last_game_date).toLocaleDateString() : null;
  const clubText = clubsLabel?.replace(/^Clubs:\s*/i, '') || guest.clubs_label || null;

  if (compact) {
    const compactClass = `rounded-lg border bg-surface-subtle px-3 py-2.5 text-left transition-colors ${
      selected
        ? 'border-info ring-1 ring-info/30'
        : selectable
          ? 'border-border hover:border-border-medium cursor-pointer'
          : 'border-border-light'
    }`;

    const compactInner = (
      <>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-semibold text-text-primary">{name}</span>
          {clubText ? (
            <span className="text-xs text-text-secondary">· {clubText}</span>
          ) : null}
        </div>
        <p className="mt-1 text-xs text-text-muted">
          <span className="text-text-secondary">
            {gameCount} game{gameCount !== 1 ? 's' : ''}
          </span>
          <span className="mx-1.5 text-border-medium">·</span>
          <span className="text-text-secondary">
            {sessionCount} session{sessionCount !== 1 ? 's' : ''}
          </span>
          {dateFrom && dateTo ? (
            <>
              <span className="mx-1.5 text-border-medium">·</span>
              <span>
                {dateFrom} – {dateTo}
              </span>
            </>
          ) : null}
        </p>
      </>
    );

    if (selectable) {
      return (
        <button type="button" onClick={onSelect} className={`w-full ${compactClass}`}>
          {compactInner}
        </button>
      );
    }

    return <div className={compactClass}>{compactInner}</div>;
  }

  const inner = (
    <>
      <div className="font-medium text-text-primary">{name}</div>
      {clubsLabel ? (
        <Caption className="mt-1 block">{clubsLabel}</Caption>
      ) : null}
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-text-secondary">
        <div>
          <span className="font-medium text-text-primary">{gameCount}</span> game
          {gameCount !== 1 ? 's' : ''}
        </div>
        <div>
          <span className="font-medium text-text-primary">{sessionCount}</span> session
          {sessionCount !== 1 ? 's' : ''}
        </div>
      </div>
      {dateFrom && dateTo ? (
        <Caption className="mt-1.5 block">
          {dateFrom} – {dateTo}
        </Caption>
      ) : null}
    </>
  );

  const cardClass = `w-full rounded-lg border transition-colors ${
    selected
      ? 'border-2 border-info bg-info-light'
      : selectable
        ? 'border-2 border-border bg-surface hover:border-border-medium'
        : 'border border-border-light bg-surface-subtle'
  }`;

  if (selectable) {
    return (
      <button
        type="button"
        onClick={onSelect}
        className={`${cardClass} p-3 text-left`}
      >
        {inner}
      </button>
    );
  }

  return <div className={`${cardClass} p-3`}>{inner}</div>;
}
